import React, { forwardRef } from 'react';
import PcteLogoNew from "assets/images/PctelogoNewOne.png";
import PcteWatermark from "assets/images/pcte_watermark.png";
import PcteSignStamp from "assets/images/pcte_sign_stamp.png";

/**
 * Custom bullet list item — renders as a flex row so the bullet glyph
 * and content text are always perfectly aligned, even in html2canvas.
 */
const BulletItem = ({ children, style = {} }) => (
    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', marginBottom: '10px', ...style }}>
        <span style={{ minWidth: '18px', width: '18px', paddingTop: '1px', flexShrink: 0, fontWeight: 'bold', fontSize: '15px', lineHeight: '1.55' }}>•</span>
        <span style={{ flex: 1, fontSize: '15px', lineHeight: '1.55' }}>{children}</span>
    </div>
);

/**
 * Custom numbered list item — same flex-row technique as BulletItem.
 */
const NumberItem = ({ number, children, style = {} }) => (
    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', marginBottom: '6px', ...style }}>
        <span style={{ minWidth: '24px', width: '24px', paddingTop: '1px', flexShrink: 0, fontWeight: 'normal', fontSize: '15px', lineHeight: '1.6' }}>{number}.</span>
        <span style={{ flex: 1, fontSize: '15px', lineHeight: '1.6' }}>{children}</span>
    </div>
);

/**
 * Logo dimensions — defined once so the PCTE logo is pixel-perfect
 * in both the html2canvas preview PDF and the jsPDF download.
 *
 * NOTE: Do NOT use objectFit here. html2canvas does not reliably honour
 * CSS `object-fit` on <img> elements and will stretch the image to fill a
 * fixed width+height box, causing the logo to look distorted in the downloaded
 * PDF. Using `width` only (with `height: auto`) preserves the natural aspect
 * ratio in every render path.
 *
 * To resize the logo, change LOGO_WIDTH only.
 */
const LOGO_WIDTH = '78px';
const LOGO_STYLE = {
    width: LOGO_WIDTH,
    height: 'auto',
    display: 'block',
    flexShrink: 0,
};

/**
 * Helper to get ordinal suffix for date formatting (e.g. 23rd)
 */
const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
        case 1: return "st";
        case 2: return "nd";
        case 3: return "rd";
        default: return "th";
    }
};

/**
 * Format date to formal style with HTML superscript for ordinal suffix (e.g., 23<sup>rd</sup>)
 */
const formatFormalHTML = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return <span>{dateString}</span>;
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'long' });
    const year = date.getFullYear();
    const suffix = getOrdinalSuffix(day);
    return (
        <span>
            {day}<sup style={{ fontSize: '10px', verticalAlign: 'super' }}>{suffix}</sup> {month}, {year}
        </span>
    );
};

/**
 * Helper to extract unmapped fields from lead data
 */
const getUnmappedField = (leadData, label) => {
    if (!leadData?.unmappedFields || !Array.isArray(leadData.unmappedFields)) {
        return null;
    }
    const field = leadData.unmappedFields.find(f =>
        f.FieldLabel?.trim().toLowerCase() === label.trim().toLowerCase() ||
        f.FieldName?.trim().toLowerCase() === label.trim().toLowerCase()
    );
    return field?.FieldValue || null;
};

export const OfferLetterPDFTemplate = forwardRef(({ formData, leadData, apiData, letterType = 'offer' }, ref) => {
    if (!formData) return null;

    // Helper to get field value from international offer letter API response (apiData.OfferLetterFields)
    const getOfferLetterFieldValue = (fieldNameOrLabel) => {
        if (!apiData?.OfferLetterFields || !Array.isArray(apiData.OfferLetterFields)) return null;
        const lower = fieldNameOrLabel.trim().toLowerCase();
        const field = apiData.OfferLetterFields.find(f =>
            f.FieldName?.trim().toLowerCase() === lower ||
            f.FieldLabel?.trim().toLowerCase() === lower
        );
        return field?.Value || null;
    };

    // Get RawJson from apiData (international student API response)
    const rawJson = (typeof apiData?.RawJson === 'string' ? (() => { try { return JSON.parse(apiData.RawJson); } catch { return {}; } })() : apiData?.RawJson) || {};

    // Standardize variables — merge first_name + last_name from dynamic fields / API response
    const firstName = formData['first_name'] || formData.firstName || getOfferLetterFieldValue('first_name') || '';
    const lastName = formData['last_name'] || formData.lastName || getOfferLetterFieldValue('last_name') || '';
    const mergedFullName = [firstName, lastName].filter(Boolean).join(' ');
    const candidateName = mergedFullName || formData.applicantName || leadData?.name || '';

    // Session and Display Lead ID
    let currentSession = apiData?.CurrentSession || '2026-27';
    if (currentSession && currentSession.includes('-') && currentSession.length === 9) {
        const parts = currentSession.split('-');
        if (parts[0].length === 4 && parts[1].length === 4) {
            currentSession = `${parts[0]}-${parts[1].substring(2)}`;
        }
    }

    const displayLeadId = apiData?.DisplayLeadId !== undefined && apiData?.DisplayLeadId !== null
        ? String(apiData.DisplayLeadId)
        : (leadData?.DisplayLeadId !== undefined && leadData?.DisplayLeadId !== null
            ? String(leadData.DisplayLeadId)
            : '13274');

    // Extract personal and contact details for applicant details block
    const gender = formData.applicantGender || formData.gender || getOfferLetterFieldValue('gender') || (leadData?.gender && leadData.gender !== 'N/A' ? leadData.gender : (getUnmappedField(leadData, "Gender") || ''));
    const fatherName = formData.fatherGuardianName || formData["Father's Name"] || getOfferLetterFieldValue("Father's Name") || formData["Guardian's Name"] || getOfferLetterFieldValue("Guardian's Name") || leadData?.FatherName || getUnmappedField(leadData, "Father's Name") || getUnmappedField(leadData, "Father Name") || getUnmappedField(leadData, "Guardian Name") || '';

    let dob = '';
    const rawDobSource = formData.dateOfBirth || formData['date_of_birth'] || getOfferLetterFieldValue('date_of_birth') || '';
    if (rawDobSource) {
        const parsedDob = new Date(rawDobSource);
        if (!isNaN(parsedDob.getTime())) {
            const d = parsedDob.getDate().toString().padStart(2, '0');
            const m = (parsedDob.getMonth() + 1).toString().padStart(2, '0');
            const y = parsedDob.getFullYear();
            dob = `${d}/${m}/${y}`;
        } else {
            dob = rawDobSource;
        }
    } else {
        const rawDob = leadData?.dob || getUnmappedField(leadData, "Date of Birth") || getUnmappedField(leadData, "DOB") || '';
        if (rawDob && String(rawDob).toUpperCase() !== 'N/A') {
            const parsedDob = new Date(rawDob);
            if (!isNaN(parsedDob.getTime())) {
                const d = parsedDob.getDate().toString().padStart(2, '0');
                const m = (parsedDob.getMonth() + 1).toString().padStart(2, '0');
                const y = parsedDob.getFullYear();
                dob = `${d}/${m}/${y}`;
            } else {
                dob = String(rawDob);
            }
        }
    }

    const country = formData.applicantCountry || formData.country || getOfferLetterFieldValue('country') || (leadData?.country && leadData.country !== 'N/A' ? leadData.country : (getUnmappedField(leadData, "Country") || ''));
    const passportNumber = formData.applicantPassportNo || formData['Passport No.'] || getOfferLetterFieldValue('Passport No.') || getOfferLetterFieldValue('Passport Details') || getUnmappedField(leadData, "Passport Number") || getUnmappedField(leadData, "Passport No") || getUnmappedField(leadData, "Passport") || '';
    const siiId = formData.applicantSiiId || formData[' SII ID'] || formData['SII ID'] || getOfferLetterFieldValue(' SII ID') || getOfferLetterFieldValue('SII ID') || getUnmappedField(leadData, "SII ID") || getUnmappedField(leadData, "SII") || '';
    const email = formData.applicantEmail || formData.email || getOfferLetterFieldValue('email') || leadData?.email || '';

    // Construct guardian email and mobile number from form input if provided, otherwise fallback
    const parts = [];
    const fatherEmail = formData.fatherEmail?.trim() || formData['Email Address']?.trim() || getOfferLetterFieldValue('Email Address') || '';
    const fatherMobile = formData.fatherMobileNumber?.trim() || formData["Father's Contact Number"]?.trim() || getOfferLetterFieldValue("Father's Contact Number") || '';
    const guardianEmail = formData.guardianEmail?.trim() || '';
    const guardianMobile = formData.guardianMobileNumber?.trim() || formData['Emergency Contact Details']?.trim() || getOfferLetterFieldValue('Emergency Contact Details') || '';

    if (fatherEmail) parts.push(fatherEmail);
    if (fatherMobile) parts.push(fatherMobile);
    if (guardianEmail) parts.push(guardianEmail);
    if (guardianMobile) parts.push(guardianMobile);

    let rawGuardianEmailMobile = parts.length > 0
        ? parts.join('; ')
        : (getUnmappedField(leadData, "Father's Mobile") || getUnmappedField(leadData, "Guardian Mobile") || getUnmappedField(leadData, "Father's Email and Mobile Number") || '');

    // Helper to check if a value is present (not empty, null, undefined, or "N/A" case-insensitive)
    const isPresent = (value) => {
        if (value === undefined || value === null) return false;
        const str = String(value).trim();
        if (str === '' || str.toUpperCase() === 'N/A' || str.toUpperCase() === 'NULL' || str.toUpperCase() === 'UNDEFINED') return false;
        return true;
    };

    // Now clean it using isPresent
    const cleanParts = String(rawGuardianEmailMobile)
        .split(/[;,]+/)
        .map(p => p.trim())
        .filter(p => isPresent(p));

    const guardianEmailMobile = cleanParts.join('; ');

    // Program & Course Name
    const programName = formData.programName || rawJson.programName || (leadData?.course && leadData.course !== 'N/A' ? leadData.course : (leadData?.program && leadData.program !== 'N/A' ? leadData.program : ''));

    // College name — comes from rawJson.collegeName returned by the API
    const collegeName = rawJson.collegeName || formData.collegeName || '';
    // Uppercase version for bank account name
    const collegeNameUpper = collegeName.toUpperCase();

    // Bank Details with static fallbacks
    const bankName = formData.bankName || rawJson.bankName || 'AXIS BANK LTD';
    const bankBranch = formData.bankBranch || rawJson.bankBranch || 'THREEKE BRANCH, LUDHIANA (INDIA)';
    const bankAccountName = formData.bankAccountName || rawJson.bankAccountName || collegeNameUpper;
    const bankAccountNumber = formData.bankAccountNumber || rawJson.bankAccountNumber || '198010100069340';
    const bankSwiftCode = formData.bankSwiftCode || rawJson.bankSwiftCode || 'AXISINBB042';
    const bankIfscCode = formData.bankIfscCode || rawJson.bankIfscCode || 'UTIB0000198';
    const bankMicrCode = formData.bankMicrCode || rawJson.bankMicrCode || '141211004';

    // Current date for issue
    const issueDateHTML = formatFormalHTML(new Date());

    // Cleaned/mocked values for deadlines based on joining/current date
    // Use the user-entered provisional registration fee date if provided, else fallback to 15 days from now
    const provisionalDateSrc = formData.provisionalRegistrationFeeDate || rawJson.provisionalRegistrationFeeDate || '';
    const registrationDeadlineHTML = provisionalDateSrc
        ? formatFormalHTML(provisionalDateSrc)
        : formatFormalHTML(new Date(Date.now() + 15 * 24 * 60 * 60 * 1000)); // 15 days from now fallback


    const programDuration = (formData.durationOfProgram ? `${formData.durationOfProgram} Years` : '') || (rawJson.durationOfProgram ? `${rawJson.durationOfProgram} Years` : '') || (apiData && (apiData.Duration ? `${apiData.Duration} Years` : '')) || '';
    const programStartSrc = formData.startDate || rawJson.startDate;
    const programEndSrc = formData.expectedEndDate || rawJson.expectedEndDate;
    const programStart = programStartSrc ? formatFormalHTML(programStartSrc) : (apiData?.StartDate ? formatFormalHTML(apiData.StartDate) : '');
    const programEnd = programEndSrc ? formatFormalHTML(programEndSrc) : (apiData?.EndDate ? formatFormalHTML(apiData.EndDate) : '');

    const hasStartDate = isPresent(programStartSrc) || (apiData && isPresent(apiData.StartDate));
    const hasEndDate = isPresent(programEndSrc) || (apiData && isPresent(apiData.EndDate));
    const hasDuration = isPresent(programDuration);

    // Fee parsing and calculation logic — check old flat apiData structure OR new RawJson structure
    const hasOldApiFees = apiData && (apiData.CourseFee !== undefined && apiData.CourseFee !== null);
    const hasRawJsonFees = rawJson.annualCourseFee !== undefined && rawJson.annualCourseFee !== null && rawJson.annualCourseFee !== '';
    const hasApiFees = hasOldApiFees || hasRawJsonFees;

    const courseFee = hasOldApiFees ? Number(apiData.CourseFee) : (hasRawJsonFees ? Number(rawJson.annualCourseFee) : 4400.00);
    const discountValue = hasOldApiFees
        ? Number(apiData.DiscountValue !== undefined ? apiData.DiscountValue : 0)
        : (hasRawJsonFees && rawJson.ScholarshipPercentage ? Number(rawJson.ScholarshipPercentage) : 0);
    const isPercentage = hasOldApiFees ? (apiData.IsDiscountTypePercentage !== undefined ? apiData.IsDiscountTypePercentage : true) : true;

    const hasScholarship = discountValue > 0;

    // Program fee after scholarship (payable per year)
    const programFeeAfterScholarship = hasOldApiFees ? (
        apiData.ProgramFeeAfterScholarship !== undefined && apiData.ProgramFeeAfterScholarship !== null ? Number(apiData.ProgramFeeAfterScholarship) :
            apiData.NetPayableFee !== undefined && apiData.NetPayableFee !== null ? Number(apiData.NetPayableFee) :
                (isPercentage ? courseFee * (1 - discountValue / 100) : courseFee - discountValue)
    ) : (hasRawJsonFees && rawJson.feeAfterScholarship !== undefined && rawJson.feeAfterScholarship !== ''
        ? Number(rawJson.feeAfterScholarship)
        : (isPercentage ? courseFee * (1 - discountValue / 100) : courseFee - discountValue)
    );

    const installmentAmount = programFeeAfterScholarship / 2;

    const healthInsurance = hasApiFees ? Number(apiData.HealthInsuranceFee !== undefined ? apiData.HealthInsuranceFee : (apiData.HealthInsurance !== undefined ? apiData.HealthInsurance : 50.00)) : 50.00;
    const examFee = hasApiFees ? Number(apiData.ExaminationFee !== undefined ? apiData.ExaminationFee : (apiData.ExamFee !== undefined ? apiData.ExamFee : 90.00)) : 90.00;

    const hostelChargesBefore = hasApiFees ? Number(apiData.HostelChargesBeforeScholarship !== undefined ? apiData.HostelChargesBeforeScholarship : (apiData.HostelFee !== undefined ? apiData.HostelFee : (apiData.HostelCharges !== undefined ? apiData.HostelCharges : 2625.00))) : 2625.00;
    const hostelScholarshipValue = hasApiFees ? Number(apiData.HostelScholarship !== undefined ? apiData.HostelScholarship : (apiData.HostelDiscountValue !== undefined ? apiData.HostelDiscountValue : 60)) : 60;
    const isHostelPercentage = hasApiFees ? (apiData.IsHostelDiscountTypePercentage !== undefined ? apiData.IsHostelDiscountTypePercentage : true) : true;

    const hostelChargesAfter = hasApiFees ? (
        apiData.HostelChargesAfterHostelScholarship !== undefined && apiData.HostelChargesAfterHostelScholarship !== null ? Number(apiData.HostelChargesAfterHostelScholarship) :
            apiData.HostelChargesAfterScholarship !== undefined && apiData.HostelChargesAfterScholarship !== null ? Number(apiData.HostelChargesAfterScholarship) :
                (isHostelPercentage ? hostelChargesBefore * (1 - hostelScholarshipValue / 100) : hostelChargesBefore - hostelScholarshipValue)
    ) : 1050.00;

    const hasHealthInsurance = hasApiFees
        ? (apiData.HealthInsuranceFee !== undefined && apiData.HealthInsuranceFee !== null && Number(apiData.HealthInsuranceFee) > 0)
        : true;

    const hasExamFee = hasApiFees
        ? (apiData.ExaminationFee !== undefined && apiData.ExaminationFee !== null && Number(apiData.ExaminationFee) > 0)
        : true;

    const hasHostel = hasApiFees
        ? (apiData.HostelChargesBeforeScholarship !== undefined && apiData.HostelChargesBeforeScholarship !== null && Number(apiData.HostelChargesBeforeScholarship) > 0)
        : true;

    const hasHostelScholarship = hasHostel && apiData && (
        apiData.HostelScholarship !== undefined && apiData.HostelScholarship !== null && Number(apiData.HostelScholarship) > 0
    );

    const adjustmentItems = ["One-Time Admission Fee"];
    if (hasHealthInsurance) adjustmentItems.push("Health Insurance Charges");
    adjustmentItems.push("Program Fee");
    if (hasHostel) adjustmentItems.push("Hostel Charges");

    let adjustmentText = "";
    if (adjustmentItems.length === 1) {
        adjustmentText = adjustmentItems[0];
    } else if (adjustmentItems.length === 2) {
        adjustmentText = `${adjustmentItems[0]} and ${adjustmentItems[1]}`;
    } else {
        adjustmentText = `${adjustmentItems.slice(0, -1).join(', ')}, and ${adjustmentItems[adjustmentItems.length - 1]}`;
    }

    // Format helpers
    const formatUSD = (val) => {
        return `USD ${Number(val).toFixed(2)}`;
    };

    const scholarshipLabel = isPercentage ? `${discountValue}%` : `USD ${discountValue.toFixed(2)}`;
    const hostelScholarshipLabel = isHostelPercentage ? `${hostelScholarshipValue}%` : `USD ${hostelScholarshipValue.toFixed(2)}`;

    const basePageStyle = {
        width: '820px',
        height: '1160px',
        padding: '65px 60px',
        backgroundColor: '#ffffff',
        color: '#000000',
        fontFamily: "'Times New Roman', Times, serif",
        position: 'relative',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        lineHeight: '1.55',
        fontSize: '15px'
    };

    const watermarkStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '450px',
        height: '450px',
        opacity: '0.55',
        pointerEvents: 'none',
        zIndex: 0,
        objectFit: 'contain'
    };

    const sectionHeaderStyle = {
        color: '#0066cc',
        fontWeight: 'bold',
        fontSize: '16.5px',
        marginTop: '15px',
        marginBottom: '6px',
        textDecoration: 'underline'
    };

    return (
        <div ref={ref} className="bg-slate-100 p-8 flex flex-col gap-8 w-fit mx-auto print:p-0 print:bg-white">

            {/* ================= PAGE 1 ================= */}
            <div style={basePageStyle}>
                {/* Watermark Logo centered behind text */}
                <img
                    src={PcteWatermark}
                    alt="PCTE Watermark"
                    style={watermarkStyle}
                    onError={(e) => {
                        e.target.src = "https://pcte.edu.in/wp-content/uploads/2019/04/PCTE-Logo-New.png";
                    }}
                />

                <div className="relative z-10 flex flex-col">
                    {/* Header */}
                    <div className="flex items-center gap-4 mb-4" style={{ fontFamily: "'Times New Roman', Times, serif" }}>
                        <div>
                            <img
                                src={PcteLogoNew}
                                alt="PCTE Logo"
                                style={LOGO_STYLE}
                                onError={(e) => {
                                    e.target.src = "https://pcte.edu.in/wp-content/uploads/2019/04/PCTE-Logo-New.png";
                                }}
                            />
                        </div>
                        <div className="flex-1 flex flex-col items-center">
                            <h1 className="text-[21.5px] font-bold text-[#003366] text-center tracking-wide leading-tight whitespace-nowrap">
                                {collegeName}
                            </h1>
                            <div className="w-full" style={{ borderBottom: '2px solid #a80000', margin: '4px 0' }}></div>
                            <p className="text-[15px] text-[#003366] text-center font-medium">
                                Affiliated to I.K.G. PTU, Jalandhar, Approved by AICTE & MoE NEW DELHI
                            </p>
                        </div>
                    </div>

                    {/* Meta Info */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '15px', fontWeight: 'bold', marginBottom: '12px' }}>
                        <div style={{ whiteSpace: 'nowrap' }}>Date of Issue: <span style={{ fontWeight: 'normal' }}>{issueDateHTML}</span></div>
                        <div style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>Ref No: <span>PCTE/LDH/{currentSession}/{displayLeadId}</span></div>
                    </div>

                    {/* Subject */}
                    <div className="mb-4">
                        <p className="text-[15px] font-bold">
                            Subject: {letterType === 'acceptance' ? 'Acceptance Letter' : 'Offer Letter'}{isPresent(programName) ? ` to study ${programName} Program` : ''}
                        </p>
                    </div>

                    {/* Applicant Details */}
                    <div className="text-[15px] space-y-3 mb-4">
                        <p className="font-bold underline text-[15.5px]">Applicant Details:</p>
                        {isPresent(candidateName) && <p><span className="font-bold">Applicant&apos;s Name:</span> <span className="uppercase">{candidateName}</span></p>}
                        {isPresent(gender) && <p><span className="font-bold">Applicant&apos;s Gender:</span> <span className="uppercase">{gender}</span></p>}
                        {isPresent(fatherName) && <p><span className="font-bold">Applicant&apos;s Father&apos;s/Guardian&apos;s Name:</span> <span className="uppercase">{fatherName}</span></p>}
                        {isPresent(dob) && <p><span className="font-bold">Applicant&apos;s Date of Birth:</span> {dob}</p>}
                        {isPresent(country) && <p><span className="font-bold">Applicant&apos;s Country:</span> <span className="uppercase">{country}</span></p>}
                        {isPresent(passportNumber) && <p><span className="font-bold">Applicant&apos;s Passport Number:</span> <span className="uppercase">{passportNumber}</span></p>}
                        {isPresent(siiId) && <p><span className="font-bold">Applicant&apos;s SII ID:</span> <span className="uppercase">{siiId}</span></p>}
                        {isPresent(email) && <p><span className="font-bold">Applicant&apos;s Email Address:</span> <span className="text-blue-600 underline">{email}</span></p>}
                        {isPresent(guardianEmailMobile) && <p><span className="font-bold">Father&apos;s/Guardian&apos;s Email Address and Mobile Number:</span> {guardianEmailMobile}</p>}
                    </div>

                    {/* Important Notice */}
                    <div className="text-[15px] leading-relaxed mb-4 font-bold">
                        IMPORTANT NOTICE: THIS {letterType === 'acceptance' ? 'ACCEPTANCE LETTER' : 'OFFER LETTER'} IS NOT VALID FOR VISA PURPOSES. <span className="font-normal">The issuance of this letter does not guarantee a visa. Obtaining a visa is solely the applicant&apos;s responsibility and is subject to approval by the Indian Embassy/ Consulate.</span>
                    </div>

                    {/* Salutation & Body */}
                    <div className="text-[15px] space-y-3 leading-relaxed mb-4">
                        <p>Dear <span className="font-bold uppercase">{candidateName}</span>,</p>
                        <p>
                            We are pleased to receive your application to study {isPresent(programName) ? (
                                <>
                                    for a <span className="font-bold">{programName} Program</span> (a full-time program)
                                </>
                            ) : (
                                "for a full-time program"
                            )} at <span className="font-bold">{collegeName}</span>, Ludhiana. We have reviewed your application and performance in the qualifying examination.{" "}
                            {hasScholarship ? (
                                <>
                                    We find that you are eligible for admission with a <span className="font-bold">{scholarshipLabel} Scholarship</span> on the Program Fee.{" "}
                                </>
                            ) : (
                                <>
                                    We find that you are eligible for admission.{" "}
                                </>
                            )}
                            On behalf of the Institution, we are pleased to issue you this <span className="font-bold">{letterType === 'acceptance' ? 'Conditional Acceptance Letter' : 'Conditional Offer Letter'}</span> for full-time study{isPresent(programName) ? (
                                <>
                                    {" "}in the <span className="font-bold">{programName} Program</span>
                                </>
                            ) : (
                                ""
                            )}.
                        </p>
                    </div>

                    {/* Offered Program Details */}
                    {(isPresent(programName) || hasDuration || hasStartDate || hasEndDate) && (
                        <div>
                            <p style={sectionHeaderStyle}>Details of Offered Program:</p>
                            <div style={{ margin: 0, fontSize: '15px' }}>
                                {isPresent(programName) && <BulletItem><span style={{ fontWeight: 'bold' }}>Program Name:</span> {programName}</BulletItem>}
                                {hasDuration && <BulletItem><span style={{ fontWeight: 'bold' }}>Duration of Program:</span> {programDuration}</BulletItem>}
                                {hasStartDate && <BulletItem><span style={{ fontWeight: 'bold' }}>Start Date:</span> {programStart}</BulletItem>}
                                {hasEndDate && <BulletItem><span style={{ fontWeight: 'bold' }}>Expected End Date:</span> {programEnd}</BulletItem>}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="relative border-t border-[#0055aa] pt-2 text-center text-[14px] text-[#003366] z-10">
                    <p className="font-normal">PCTE Campus -2, Near Baddowal Cantt., Ferozepur Road, Ludhiana-142021 India. URL: <a href="https://www.pcte.edu.in" target="_blank" rel="noopener noreferrer" className="underline hover:opacity-80">www.pcte.edu.in</a></p>
                    <p className="mt-0.5 font-normal">E-mail: <a href="mailto:info@pcte.edu.in" className="underline hover:opacity-80">info@pcte.edu.in</a> Ph.: +91-161-28885060 Fax: +91-161-2888500</p>
                </div>
            </div>

            {/* ================= PAGE 2 ================= */}
            <div style={basePageStyle}>
                {/* Watermark Logo centered behind text */}
                <img
                    src={PcteWatermark}
                    alt="PCTE Watermark"
                    style={watermarkStyle}
                    onError={(e) => {
                        e.target.src = "https://pcte.edu.in/wp-content/uploads/2019/04/PCTE-Logo-New.png";
                    }}
                />

                <div className="relative z-10 flex flex-col">
                    {/* Fee Details Section A */}
                    <div>
                        <p style={sectionHeaderStyle}>Fee Details of Offered Program (A):</p>
                        <div style={{ margin: 0, fontSize: '15px' }}>
                            <BulletItem><span style={{ fontWeight: 'bold' }}>Program Fee per year:</span> {formatUSD(courseFee)}</BulletItem>
                            {hasScholarship && (
                                <>
                                    <BulletItem><span style={{ fontWeight: 'bold' }}>Scholarship per year:</span> {scholarshipLabel}</BulletItem>
                                    <BulletItem><span style={{ fontWeight: 'bold' }}>Program Fee after Scholarship (payable per year):</span> {formatUSD(programFeeAfterScholarship)}</BulletItem>
                                </>
                            )}
                        </div>
                        {hasScholarship ? (
                            <p className="text-[13.5px] font-bold italic mt-2 pl-2">
                                * Program fee can be paid in two-installments of USD {installmentAmount % 1 === 0 ? installmentAmount.toFixed(0) : installmentAmount.toFixed(2)} (semester-wise) before deadline period.
                            </p>
                        ) : (
                            <p className="text-[13.5px] font-bold italic mt-2 pl-2">
                                * Program fee can be paid in two-installments of USD {(courseFee / 2) % 1 === 0 ? (courseFee / 2).toFixed(0) : (courseFee / 2).toFixed(2)} (semester-wise) before deadline period.
                            </p>
                        )}
                    </div>

                    {/* Other Fee Details Section B — always rendered with static values */}
                    <div className="mt-4">
                        <p style={sectionHeaderStyle}>Other Fee Details (B):</p>
                        <div style={{ margin: 0, fontSize: '15px' }}>
                            <BulletItem><span style={{ fontWeight: 'bold' }}>University Examination Fee:</span> USD 90 per year, subject to change as per the regulations and fee structure prescribed by the state university, I.K. Gujral Punjab Technical University (PTU)</BulletItem>
                            <BulletItem><span style={{ fontWeight: 'bold' }}>Hostel Charges before Scholarship per year:</span> USD 2625.00</BulletItem>
                            <BulletItem><span style={{ fontWeight: 'bold' }}>Hostel Scholarship per year:</span> 60%</BulletItem>
                            <BulletItem><span style={{ fontWeight: 'bold' }}>Hostel Charges after Hostel Scholarship per year:</span> USD 1050.00 (In Case the Hostel Service is availed)</BulletItem>
                        </div>
                        <div style={{ fontSize: '13.5px', marginTop: '8px', paddingLeft: '8px', lineHeight: '1.6' }}>
                            <p>
                                <span style={{ fontWeight: 'bold' }}>*Residential Type:</span> Air-conditioned room rent (triple sharing), food (3-times a day), refrigerator, mattress, curtains, beds, wardrobe, study table, chair, gym, and free Wi-Fi. The student is required to pay hostel electricity charges, which may vary from USD 10 to USD 50 per month (as per usage) in case the hostel facility is availed.
                            </p>
                            <p style={{ fontWeight: 'bold', fontStyle: 'italic' }}>
                                *Subject to yearly revision
                            </p>
                        </div>
                    </div>


                    {/* Provisional Registration Fee */}
                    {letterType !== 'acceptance' && (
                        <div className="mt-4">
                            <p style={sectionHeaderStyle}>Provisional Registration Fee:</p>
                            <p className="text-[15px] leading-relaxed">
                                You must submit a <span className="font-bold">Provisional Registration Fee of USD 250.00</span> by <span className="font-bold">{registrationDeadlineHTML}</span>. This fee secures your place in the program and will be adjusted against the {adjustmentText}.
                            </p>
                            <p className="text-[15px] font-bold mt-2">
                                The Balance Fee must be paid immediately after the Acceptance Letter is issued or before reporting to the Institution, whichever is earlier.
                            </p>
                        </div>
                    )}

                    {/* How to Pay Fee */}
                    <div className="mt-4">
                        <p style={sectionHeaderStyle}>How to Pay Fee:</p>
                        <p className="text-[15px] mb-2">
                            All international applicants must pay their fees in USD using the following details:
                        </p>
                        <div style={{ backgroundColor: '#f8fafc', border: '1px solid #cbd5e1', padding: '12px', borderRadius: '4px', fontSize: '13.5px' }}>
                            <p style={{ fontWeight: 'bold', color: '#003366', textTransform: 'uppercase', marginBottom: '6px', borderBottom: '1px solid #cbd5e1', paddingBottom: '4px' }}>Beneficiary Bank Details</p>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13.5px' }}>
                                <tbody>
                                    {bankName && (
                                        <tr>
                                            <td style={{ fontWeight: 'bold', width: '38%', paddingBottom: '3px', verticalAlign: 'top' }}>Beneficiary Bank&apos;s Name:</td>
                                            <td style={{ paddingBottom: '3px', verticalAlign: 'top' }}>{bankName}</td>
                                        </tr>
                                    )}
                                    {bankBranch && (
                                        <tr>
                                            <td style={{ fontWeight: 'bold', paddingBottom: '3px', verticalAlign: 'top' }}>Beneficiary Bank&apos;s Address:</td>
                                            <td style={{ paddingBottom: '3px', verticalAlign: 'top' }}>{bankBranch}</td>
                                        </tr>
                                    )}
                                    {bankAccountName && (
                                        <tr>
                                            <td style={{ fontWeight: 'bold', paddingBottom: '3px', verticalAlign: 'top' }}>Beneficiary Account Name:</td>
                                            <td style={{ paddingBottom: '3px', verticalAlign: 'top' }}>{bankAccountName}</td>
                                        </tr>
                                    )}
                                    {bankAccountNumber && (
                                        <tr>
                                            <td style={{ fontWeight: 'bold', paddingBottom: '3px', verticalAlign: 'top' }}>Beneficiary Account Number:</td>
                                            <td style={{ fontFamily: 'monospace', fontWeight: 'bold', paddingBottom: '3px', verticalAlign: 'top' }}>{bankAccountNumber}</td>
                                        </tr>
                                    )}
                                    {bankSwiftCode && (
                                        <tr>
                                            <td style={{ fontWeight: 'bold', paddingBottom: '3px', verticalAlign: 'top' }}>SWIFT Code:</td>
                                            <td style={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#2563eb', paddingBottom: '3px', verticalAlign: 'top' }}>{bankSwiftCode}</td>
                                        </tr>
                                    )}
                                    {bankIfscCode && (
                                        <tr>
                                            <td style={{ fontWeight: 'bold', paddingBottom: '3px', verticalAlign: 'top' }}>IFSC Code:</td>
                                            <td style={{ fontFamily: 'monospace', fontWeight: 'bold', paddingBottom: '3px', verticalAlign: 'top' }}>{bankIfscCode}</td>
                                        </tr>
                                    )}
                                    {bankMicrCode && (
                                        <tr>
                                            <td style={{ fontWeight: 'bold', paddingBottom: '3px', verticalAlign: 'top' }}>MICR Code:</td>
                                            <td style={{ fontFamily: 'monospace', paddingBottom: '3px', verticalAlign: 'top' }}>{bankMicrCode}</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                    </div>
                </div>

                {/* Footer */}
                <div className="relative border-t border-[#0055aa] pt-2 text-center text-[14px] text-[#003366] z-10">
                    <p className="font-normal">PCTE Campus -2, Near Baddowal Cantt., Ferozepur Road, Ludhiana-142021 India. URL: <a href="https://www.pcte.edu.in" target="_blank" rel="noopener noreferrer" className="underline hover:opacity-80">www.pcte.edu.in</a></p>
                    <p className="mt-0.5 font-normal">E-mail: <a href="mailto:info@pcte.edu.in" className="underline hover:opacity-80">info@pcte.edu.in</a> Ph.: +91-161-28885060 Fax: +91-161-2888500</p>
                </div>
            </div>

            {/* ================= PAGE 3 ================= */}
            <div style={basePageStyle}>
                {/* Watermark Logo centered behind text */}
                <img
                    src={PcteWatermark}
                    alt="PCTE Watermark"
                    style={watermarkStyle}
                    onError={(e) => {
                        e.target.src = "https://pcte.edu.in/wp-content/uploads/2019/04/PCTE-Logo-New.png";
                    }}
                />

                <div className="relative z-10 flex flex-col">
                    {/* Additional Conditions for Admission */}
                    <div>
                        <p style={sectionHeaderStyle}>Additional Conditions for Admission:</p>
                        <div style={{ margin: 0, fontSize: '15px', lineHeight: '1.6' }}>
                            <BulletItem style={{ marginBottom: '8px' }}>
                                <span style={{ fontWeight: 'bold' }}>Conditional Admission:</span> This is a <span style={{ fontWeight: 'bold' }}>{letterType === 'acceptance' ? 'Conditional Acceptance Letter' : 'Conditional Offer Letter'}</span>, and admission is subject to verification of original documents, confirmation of fee payment, and compliance with all admission requirements. Any misrepresentation will result in immediate cancellation <span style={{ fontWeight: 'bold' }}>without refund.</span>
                            </BulletItem>
                            <BulletItem style={{ marginBottom: '8px' }}>
                                <span style={{ fontWeight: 'bold' }}>Mandatory Acceptance Letter:</span> The applicant must sign and return an <span style={{ fontWeight: 'bold' }}>&quot;{letterType === 'acceptance' ? 'Acceptance Letter' : 'Acceptance of Offer Letter'}&quot;</span> before finalizing admission, confirming he/she understands all terms, fees, and refund policies.
                            </BulletItem>
                            <BulletItem style={{ marginBottom: '8px' }}>
                                <span style={{ fontWeight: 'bold' }}>Equivalence Certificate:</span> Students may be required to obtain an <span style={{ fontWeight: 'bold' }}>Equivalence Certificate from the UGC</span> (University Grants Commission), subject to the applicable requirements of I.K. Gujral Punjab Technical University (IKGPTU/PTU).
                            </BulletItem>
                            <BulletItem style={{ marginBottom: '8px' }}>
                                <span style={{ fontWeight: 'bold' }}>Medical Insurance:</span> Medical insurance is included in the programme. Students are required to register for the medical insurance themselves upon arrival in India. The registration process and its completion are the sole responsibility of the student.
                            </BulletItem>
                            <BulletItem style={{ marginBottom: '8px' }}>
                                <span style={{ fontWeight: 'bold' }}>Strict FRRO Registration Compliance:</span> As per Government of India guidelines, all international students <span style={{ fontWeight: 'bold' }}>must apply for FRRO registration within 14 days of arrival</span> and submit proof to the institution within 30 days.
                            </BulletItem>
                            <BulletItem style={{ marginBottom: '8px' }}>
                                <span style={{ fontWeight: 'bold' }}>Bank Statement Requirement for Visa &amp; FRRO:</span> Applicants must submit a <span style={{ fontWeight: 'bold' }}>bank statement duly attested by their bank</span> at the time of FRRO registration, as per Indian visa regulations.
                            </BulletItem>
                        </div>
                    </div>

                    {/* Important Deadlines & Refund Policy */}
                    <div className="mt-3">
                        <p style={sectionHeaderStyle}>Important Deadlines & Refund Policy:</p>
                        <div style={{ margin: 0, fontSize: '15px', lineHeight: '1.6' }}>
                            <BulletItem style={{ marginBottom: '6px' }}>
                                {letterType === 'acceptance' ? (
                                    <>
                                        <span style={{ fontWeight: 'bold' }}>Pending Fee Deadline:</span> The pending fee of 1st semester should be paid within 30 days of the issuance of this letter or upon arrival in the college. Failure to meet this deadline will result in <span style={{ fontWeight: 'bold', color: '#dc2626' }}>automatic cancellation</span> of this offer.
                                    </>
                                ) : (
                                    <>
                                        <span style={{ fontWeight: 'bold' }}>Registration Fee Deadline:</span> The One-Time Registration Fee of USD 250 must be paid within the timeline specified above. Failure to make the payment within the stipulated deadline will result in the <span style={{ fontWeight: 'bold', color: '#dc2626' }}>automatic cancellation</span> of this offer.
                                    </>
                                )}
                            </BulletItem>
                            <BulletItem style={{ marginBottom: '6px' }}>
                                <span style={{ fontWeight: 'bold' }}>Refund Policy:</span> Refund requests will <span style={{ fontWeight: 'bold' }}>only be processed</span> as per institutional guidelines issued by the <span style={{ fontWeight: 'bold' }}>Registrar&apos;s Office.</span> Any request beyond the prescribed format will <span style={{ fontWeight: 'bold' }}>not</span> be entertained.
                            </BulletItem>
                        </div>
                    </div>

                    {/* Instructions while Reporting/Joining the Institution */}
                    <div className="mt-3">
                        <p style={sectionHeaderStyle}>Instructions while Reporting/Joining the Institution:</p>
                        <div style={{ margin: 0, fontSize: '15px', lineHeight: '1.6' }}>
                            <NumberItem number={1} style={{ marginBottom: '6px' }}>
                                <span style={{ fontWeight: 'bold' }}>Fee Payment Before Joining:</span> The <span style={{ fontWeight: 'bold' }}>first-semester program fee{hasHostel ? " and hostel charges" : ""}</span> must be cleared before arrival.
                            </NumberItem>
                            <NumberItem number={2} style={{ marginBottom: '6px' }}>
                                <span style={{ fontWeight: 'bold' }}>Original Documents:</span> The student must bring original <span style={{ fontWeight: 'bold' }}>mark sheets, transcripts, and certificates</span> for verification.
                            </NumberItem>
                            <NumberItem number={3} style={{ marginBottom: '6px' }}>
                                <span style={{ fontWeight: 'bold' }}>Student Visa Requirement:</span> The applicant must carry their <span style={{ fontWeight: 'bold' }}>Passport with a Student Visa</span> endorsed in the name of Punjab College of Technical Education (except for Bhutan/ Nepal residents).
                            </NumberItem>
                            <NumberItem number={4} style={{ marginBottom: '6px' }}>
                                <span style={{ fontWeight: 'bold' }}>FRRO Registration:</span> International students must apply for <span style={{ fontWeight: 'bold' }}>FRRO registration within 14 days of arrival</span> and submit a copy to the Institution within 30 days (not required for Bhutan/ Nepal students).
                            </NumberItem>
                            <NumberItem number={5} style={{ marginBottom: '6px' }}>
                                <span style={{ fontWeight: 'bold' }}>Bank Statements for FRRO:</span> Parents/Guardians&apos; <span style={{ fontWeight: 'bold' }}>attested bank statements</span> must be submitted for visa compliance.
                            </NumberItem>
                        </div>
                    </div>

                    {/* Visa Application */}
                    <div className="mt-3">
                        <p style={sectionHeaderStyle}>Visa Application:</p>
                        <p className="text-[15px] leading-relaxed">
                            Students must apply for a <span className="font-bold">Student Visa</span> at the nearest <span className="font-bold">Indian Embassy/ Consulate</span> in their home country. Processing time may take <span className="font-bold">3-6 weeks</span>, so early application is advised.
                        </p>
                    </div>
                </div>

                {/* Footer */}
                <div className="relative border-t border-[#0055aa] pt-2 text-center text-[14px] text-[#003366] z-10">
                    <p className="font-normal">PCTE Campus -2, Near Baddowal Cantt., Ferozepur Road, Ludhiana-142021 India. URL: <a href="https://www.pcte.edu.in" target="_blank" rel="noopener noreferrer" className="underline hover:opacity-80">www.pcte.edu.in</a></p>
                    <p className="mt-0.5 font-normal">E-mail: <a href="mailto:info@pcte.edu.in" className="underline hover:opacity-80">info@pcte.edu.in</a> Ph.: +91-161-28885060 Fax: +91-161-2888500</p>
                </div>
            </div>

            {/* ================= PAGE 4 — Final Confirmation ================= */}
            <div style={basePageStyle}>
                {/* Watermark */}
                <img
                    src={PcteWatermark}
                    alt="PCTE Watermark"
                    style={watermarkStyle}
                    onError={(e) => {
                        e.target.src = "https://pcte.edu.in/wp-content/uploads/2019/04/PCTE-Logo-New.png";
                    }}
                />

                <div className="relative z-10 flex flex-col">

                    {/* Final Confirmation header */}
                    <p style={{ ...sectionHeaderStyle, marginTop: '8px', marginBottom: '18px' }}>Final Confirmation:</p>

                    {/* Contact paragraph */}
                    <p style={{ fontSize: '15px', lineHeight: '1.65', marginBottom: '16px' }}>
                        For any further clarification, visit{' '}
                        <a href="https://www.pcte.edu.in" target="_blank" rel="noopener noreferrer" style={{ color: '#0044cc', textDecoration: 'underline' }}>www.pcte.edu.in</a>{' '}
                        or contact{' '}
                        <a href="mailto:internationalaffairs@pcte.edu.in" style={{ color: '#0044cc', textDecoration: 'underline' }}>internationalaffairs@pcte.edu.in</a>.
                        {' '}You can also reach Ms. Harpreet K Kang,{' '}
                        <span style={{ fontWeight: 'bold' }}>Dean – International Affairs</span>, at{' '}
                        <span style={{ fontWeight: 'bold' }}>+91-9855111455</span>.
                    </p>

                    {/* Welcome */}
                    <p style={{ fontSize: '15px', fontWeight: 'bold', marginBottom: '16px' }}>We welcome you to PCTE!</p>

                    {/* First sign-off block */}
                    <p style={{ fontSize: '15px', marginBottom: '4px' }}>Best Wishes</p>
                    <p style={{ fontSize: '15px', marginBottom: '4px' }}>Dr. K. N. S. Kang</p>
                    <p style={{ fontSize: '15px', marginBottom: '24px' }}>President-cum-Director General</p>

                    {/* Second sign-off block with signature + stamp */}
                    <p style={{ fontSize: '15px', fontWeight: 'bold', marginBottom: '12px' }}>Best Wishes</p>

                    {/* Signature & stamp image */}
                    <div style={{ marginBottom: '10px' }}>
                        <img
                            src={PcteSignStamp}
                            alt="Signature and Stamp"
                            style={{ width: '200px', height: 'auto', display: 'block' }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                        />
                    </div>

                    {/* Name and designation */}
                    <p style={{ fontSize: '15px', fontWeight: 'bold', marginBottom: '2px' }}>Dr. K. N. S. Kang</p>
                    <p style={{ fontSize: '15px', fontWeight: 'bold' }}>President-cum-Director General</p>

                </div>

                {/* Footer */}
                <div className="relative border-t border-[#0055aa] pt-2 text-center text-[14px] text-[#003366] z-10">
                    <p className="font-normal">PCTE Campus -2, Near Baddowal Cantt., Ferozepur Road, Ludhiana-142021 India. URL: <a href="https://www.pcte.edu.in" target="_blank" rel="noopener noreferrer" className="underline hover:opacity-80">www.pcte.edu.in</a></p>
                    <p className="mt-0.5 font-normal">E-mail: <a href="mailto:info@pcte.edu.in" className="underline hover:opacity-80">info@pcte.edu.in</a> Ph.: +91-161-28885060 Fax: +91-161-2888500</p>
                </div>
            </div>

        </div>
    );
});

OfferLetterPDFTemplate.displayName = 'OfferLetterPDFTemplate';
